/**
 * Pure helpers of the Git tab (GitTab.jsx) over GitService's overview: a repo's counts and remotes, a remote's web page,
 * Flow's clone URLs and the one-time clone hint with a token.
 */
import { ago } from '../../utils/blameModel';

/** Files with any change (the server's `changed`; staged and untracked when it only gives those). */
export function changedCount(repo) {
  if (!repo) { return 0; }
  const changed = Number(repo.changed) || 0;
  return changed || (Number(repo.staged) || 0) + (Number(repo.untracked) || 0);
}

/** The external remotes (Flow is never one of them: it is the repo itself). */
export const remotesOf = (repo) => (Array.isArray(repo?.remotes) ? repo.remotes.filter((r) => r && r.name && r.name !== 'flow') : []);

/** The remote Push and Pull use by default: the upstream's, `origin`, else the first. */
export function defaultRemote(repo) {
  const remotes = remotesOf(repo);
  if (!remotes.length) { return null; }
  const upstream = typeof repo.upstream === 'string' ? repo.upstream.split('/')[0] : null;
  return (remotes.find((r) => r.name === upstream) || remotes.find((r) => r.name === 'origin') || remotes[0]).name;
}

/** `https://host/path` from an https or ssh (`git@host:path`) URL, `.git` and any user info left out; null if neither. */
export function webUrlFromRemote(url) {
  const raw = String(url || '').trim();
  if (!raw) { return null; }
  const ssh = /^(?:ssh:\/\/)?[^@/\s]+@([^:/\s]+)[:/](.+)$/.exec(raw);
  if (ssh && !/^https?:/i.test(raw)) { return `https://${ssh[1]}/${ssh[2].replace(/\.git$/i, '').replace(/^\/+/, '')}`; }
  try {
    const parsed = new URL(raw);
    if (!/^https?:$/.test(parsed.protocol)) { return null; }
    return `${parsed.protocol}//${parsed.host}${parsed.pathname.replace(/\.git$/i, '').replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

/** A remote's web page: the server's `webUrl`, else read from its URL. */
export const remoteWebUrl = (remote) => remote?.webUrl || webUrlFromRemote(remote?.url);

/** The host a remote lives on ("gitlab.com"), for "Open on gitlab.com". */
export function remoteHost(remote) {
  if (remote?.host) { return remote.host; }
  const web = remoteWebUrl(remote);
  if (!web) { return null; }
  try { return new URL(web).host; } catch { return null; }
}

/** Flow's clone base as an absolute URL (a relative one is on this origin). */
export function absoluteCloneBase(cloneBase, origin = typeof window !== 'undefined' ? window.location.origin : '') {
  const value = String(cloneBase || '').trim().replace(/\/+$/, '');
  if (!value) { return null; }
  return value.startsWith('/') ? `${origin}${value}` : value;
}

/** A service's clone URL on Flow: `<cloneBase>/<service>.git`. */
export function flowCloneUrl(cloneBase, service, origin) {
  const absolute = absoluteCloneBase(cloneBase, origin);
  return absolute && service ? `${absolute}/${encodeURIComponent(service)}.git` : null;
}

/** `git clone https://git:<token>@host/... <service>`: the one command to paste, with the token as the password. */
export function cloneCommand(url, token, service) {
  if (!url) { return ''; }
  const withToken = token ? url.replace(/^(https?:\/\/)/i, `$1git:${token}@`) : url;
  return `git clone ${withToken}${service ? ` ${service}` : ''}`;
}

/** "Fix login · 2 h ago", or null without a commit. */
export function lastCommitText(repo, now = Date.now()) {
  const last = repo?.lastCommit;
  if (!last || !last.subject) { return null; }
  const when = last.date ? ago(last.date, now) : '';
  return when ? `${last.subject} · ${when}` : last.subject;
}

/** A branch or tag name git accepts (the server checks too): no spaces, no `..`, no leading `-`, no `~^:?*[\`. */
export function refNameProblem(raw, taken = []) {
  const name = String(raw || '').trim();
  if (!name) { return 'Give it a name.'; }
  if (/[\s~^:?*[\\]/.test(name) || name.includes('..') || name.startsWith('-') || name.startsWith('/') || name.endsWith('/') || name.endsWith('.') || name.endsWith('.lock') || name.includes('@{')) {
    return 'Use letters, digits, dots, dashes, underscores and slashes, no spaces.';
  }
  if (taken.includes(name)) { return `${name} already exists.`; }
  return null;
}

/** A remote's name: a short word. */
export function remoteNameProblem(raw, taken = []) {
  const name = String(raw || '').trim();
  if (!name) { return 'Give the remote a name.'; }
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/.test(name)) { return 'Letters, digits, dots, dashes and underscores only.'; }
  if (name === 'flow') { return 'flow is Flow itself: pick another name.'; }
  if (taken.includes(name)) { return `${name} already exists.`; }
  return null;
}

/** "3 services: 2 pushed, 1 failed (api: …)" for the bulk actions. */
export function bulkSummary(verb, results) {
  const ok = results.filter((r) => r.ok).length;
  const failed = results.filter((r) => !r.ok);
  const head = `${verb} ${ok} of ${results.length}`;
  return failed.length ? `${head}. Failed: ${failed.map((r) => `${r.label}: ${r.error?.message || 'error'}`).join('; ')}` : `${head}.`;
}

/**
 * Remote branches worth offering in the branch menu: `origin/feature/x` entries (never `<remote>/HEAD`, never Flow's
 * own remote) whose local branch does not exist yet; each `{ ref, remote, name }`.
 */
export function remoteBranchesOf(repo) {
  const local = new Set(Array.isArray(repo?.branches) ? repo.branches : []);
  const known = new Set((Array.isArray(repo?.remotes) ? repo.remotes : []).map((r) => r?.name));
  const list = Array.isArray(repo?.remoteBranches) ? repo.remoteBranches : [];
  return list
    .map((ref) => {
      const slash = String(ref).indexOf('/');
      return slash > 0 ? { ref, remote: ref.slice(0, slash), name: ref.slice(slash + 1) } : null;
    })
    .filter((b) => b && b.name !== 'HEAD' && b.remote !== 'flow' && (known.size === 0 || known.has(b.remote)) && !local.has(b.name));
}

/**
 * The Tags dialog's rows: the local tags and what each remote has (`remoteSets`: `{ <remote>: Set of names } `, a remote
 * that could not be read left out). `onRemotes`: the remotes that have it; `state`: `local` (no remote read), `synced`
 * (here and on a remote), `only-here` (a remote was read and none has it), `remote-only` (not fetched yet).
 */
export function tagRows(localTags, remoteSets = {}) {
  const names = Object.keys(remoteSets);
  const local = (Array.isArray(localTags) ? localTags : []).map((t) => (typeof t === 'string' ? { name: t } : t)).filter((t) => t?.name);
  const rows = local.map((tag) => {
    const onRemotes = names.filter((remote) => remoteSets[remote].has(tag.name));
    let state = 'local';
    if (names.length) { state = onRemotes.length ? 'synced' : 'only-here'; }
    return { ...tag, onRemotes, state };
  });
  const have = new Set(local.map((t) => t.name));
  const remoteOnly = new Map();
  names.forEach((remote) => remoteSets[remote].forEach((name) => {
    if (!have.has(name)) { remoteOnly.set(name, [...(remoteOnly.get(name) || []), remote]); }
  }));
  [...remoteOnly].sort(([a], [b]) => a.localeCompare(b)).forEach(([name, onRemotes]) => rows.push({ name, onRemotes, state: 'remote-only' }));
  return rows;
}

/**
 * How the current branch stands on one remote, as far as the overview tells: `{ kind, text, title? }`. The overview
 * compares with the upstream only (`ahead`/`behind`), so the upstream's remote gets `↑a ↓b` (`tracked`); another remote
 * says whether it has the branch (`present`, not compared), lacks it (`missing`) or was never fetched (`unfetched`).
 */
export function remoteStatusOf(repo, remote) {
  const name = remote?.name;
  const branch = repo?.branch || null;
  const upstream = typeof repo?.upstream === 'string' ? repo.upstream : null;
  if (upstream && upstream.split('/')[0] === name) {
    return { kind: 'tracked', text: `↑${Number(repo.ahead) || 0} ↓${Number(repo.behind) || 0}`, title: `Compared with ${upstream}` };
  }
  const refs = (Array.isArray(repo?.remoteBranches) ? repo.remoteBranches : []).filter((ref) => String(ref).startsWith(`${name}/`));
  if (!refs.length) { return { kind: 'unfetched', text: 'not fetched' }; }
  if (!branch) { return { kind: 'present', text: 'detached: not compared' }; }
  // vfs-service's overview compares the branch with every remote that has it (remote.ahead / remote.behind); older ones
  // only with the upstream.
  if (refs.includes(`${name}/${branch}`) && Number.isFinite(remote?.ahead) && Number.isFinite(remote?.behind)) {
    return { kind: 'compared', text: `↑${remote.ahead} ↓${remote.behind}`, title: `Compared with ${name}/${branch} (not the upstream)` };
  }
  if (refs.includes(`${name}/${branch}`)) { return { kind: 'present', text: `has ${branch}, no upstream`, title: `${name}/${branch} is not the upstream of ${branch}: not compared` }; }
  return { kind: 'missing', text: `no ${branch} there yet` };
}
